import React from "react";
import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSCardHeader from "views/system-design/DSCardHeader";
import DSTypography from "views/system-design/DSTypography";
import DSChip from "views/system-design/DSChip";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import type { TestStatus, StoryResult } from "../types";

/**
 * Per-device status information for real-time test progress display
 */
export interface DeviceStatus {
  deviceId: string;
  status: TestStatus;
  step: string;
  errorsDetected: number;
}

/**
 * Story progress item for real-time story execution tracking
 */
export interface StoryProgressItem {
  storyId: string;
  storyName: string;
  storyIndex: number;
  totalStories: number;
  status: "pending" | "running" | "pass" | "fail" | "error" | "timeout";
  currentStep?: number;
  latestReasoning?: string;
  result?: StoryResult;
}

interface TestProgressProps {
  deviceStatuses: Record<string, DeviceStatus>;
  isTestRunning: boolean;
  storyProgress?: Record<string, Record<string, StoryProgressItem>>;
}

/**
 * TestProgress component - displays real-time per-device test progress
 *
 * Shows each device's current status with:
 * - Colored status chip (grey=pending, amber=running, green=completed, red=failed)
 * - Human-readable step description
 * - Progress spinner for active states
 * - Error counter (red if errors detected)
 *
 * Rendered during test execution to provide live feedback.
 */
const TestProgress: React.FC<TestProgressProps> = ({
  deviceStatuses,
  isTestRunning,
  storyProgress,
}) => {
  const statusEntries = Object.values(deviceStatuses);

  // Don't render anything if no device statuses and not running
  if (statusEntries.length === 0 && !isTestRunning) {
    return null;
  }

  // Map TestStatus to chip color
  const getStatusColor = (
    status: TestStatus,
  ): "default" | "warning" | "success" | "error" => {
    switch (status) {
      case "running-reset":
      case "running-install":
      case "monitoring":
      case "analyzing":
      case "connecting-wifi":
        return "warning"; // Amber for active states
      case "completed":
        return "success"; // Green
      case "failed":
        return "error"; // Red
      case "pending":
      default:
        return "default"; // Grey
    }
  };

  // Determine if status is an active (running) state
  const isActiveStatus = (status: TestStatus): boolean => {
    return (
      status === "running-reset" ||
      status === "running-install" ||
      status === "monitoring" ||
      status === "analyzing" ||
      status === "connecting-wifi"
    );
  };

  // Map story status to chip color
  const getStoryStatusColor = (
    status: StoryProgressItem["status"],
  ): "default" | "warning" | "success" | "error" => {
    switch (status) {
      case "pending":
        return "default";
      case "running":
        return "warning";
      case "pass":
        return "success";
      case "fail":
      case "error":
      case "timeout":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <DSBox sx={{ mt: 4 }}>
      <DSTypography variant="h6" sx={{ mb: 2 }}>
        Test Progress
      </DSTypography>

      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {statusEntries.map((deviceStatus) => (
          <DSCard key={deviceStatus.deviceId}>
            <DSCardHeader title={deviceStatus.deviceId} />
            <DSCardContent>
              <DSBox
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                {/* Status chip */}
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSChip
                    label={deviceStatus.status}
                    color={getStatusColor(deviceStatus.status)}
                    size="small"
                  />
                </DSBox>

                {/* Step description with spinner for active states */}
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSTypography variant="body2" color="text.secondary">
                    {deviceStatus.step}
                  </DSTypography>
                  {isActiveStatus(deviceStatus.status) && (
                    <DSCircularProgress size={20} />
                  )}
                </DSBox>

                {/* Error counter */}
                <DSTypography
                  variant="body2"
                  sx={{
                    color:
                      deviceStatus.errorsDetected > 0
                        ? "error.main"
                        : "text.primary",
                  }}
                >
                  Errors detected: {deviceStatus.errorsDetected}
                </DSTypography>

                {/* Story progress section */}
                {storyProgress?.[deviceStatus.deviceId] &&
                  Object.keys(storyProgress[deviceStatus.deviceId]).length >
                    0 && (
                    <DSBox sx={{ mt: 2 }}>
                      <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                        Story Progress
                      </DSTypography>
                      <DSBox
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {Object.values(storyProgress[deviceStatus.deviceId])
                          .sort((a, b) => a.storyIndex - b.storyIndex)
                          .map((story) => (
                            <DSBox
                              key={story.storyId || story.storyIndex}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              {/* Story status row */}
                              <DSBox
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <DSChip
                                  label={story.status}
                                  color={getStoryStatusColor(story.status)}
                                  size="small"
                                />
                                <DSTypography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {story.storyName ||
                                    `Story ${story.storyIndex + 1}`}
                                </DSTypography>
                              </DSBox>

                              {/* Current step indicator for running stories */}
                              {story.status === "running" &&
                                story.currentStep && (
                                  <DSBox
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      pl: 1,
                                    }}
                                  >
                                    <DSCircularProgress size={16} />
                                    <DSTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Step {story.currentStep}
                                    </DSTypography>
                                  </DSBox>
                                )}

                              {/* Latest reasoning for running stories */}
                              {story.status === "running" &&
                                story.latestReasoning && (
                                  <DSTypography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: "italic", pl: 1 }}
                                  >
                                    {story.latestReasoning.length > 120
                                      ? `${story.latestReasoning.substring(0, 120)}...`
                                      : story.latestReasoning}
                                  </DSTypography>
                                )}
                            </DSBox>
                          ))}
                      </DSBox>
                    </DSBox>
                  )}
              </DSBox>
            </DSCardContent>
          </DSCard>
        ))}
      </DSBox>
    </DSBox>
  );
};

export default TestProgress;
